/* ==========================================================================
   Rook UI Core — Accordion Controller
   Web Component: <rk-accordion-element>

   Anima a altura do <details> ao abrir/fechar. Suporta modo exclusivo:
   se o elemento estiver dentro de um container com [data-accordion-exclusive],
   abrir um item fecha (com animação) o irmão que estiver aberto — só um por vez.
   ========================================================================== */

import { RkComponent } from '@rook/component';

class RkAccordionElement extends RkComponent {
    static hydration = 'idle';

    setup() {
        this.details = this.querySelector('details');

        const summary = this.details ? this.details.querySelector('summary') : null;
        // Conteúdo = elemento logo após o <summary> (funciona para
        // .rk-accordion__content e .rk-faq-item__content sem acoplar a classe).
        this.content = summary ? summary.nextElementSibling : null;

        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;

        if (!this.details || !this.content) return;

        // Modo exclusivo: se já nascer aberto mas um irmão anterior também
        // estiver aberto, fecha este na marra (só o primeiro permanece aberto).
        if (this.details.open) {
            const scope = this.#exclusiveScope();
            if (scope) {
                const firstOpen = scope.querySelector('details[open]');
                if (firstOpen && firstOpen !== this.details) {
                    this.details.open = false;
                }
            }
        }

        this.delegate({
            'click summary': this.onClick,
        });
    }

    onClick(event) {
        event.preventDefault();

        this.details.style.overflow = 'hidden';

        if (this.isClosing || !this.details.open) {
            this.open();
        } else if (this.isExpanding || this.details.open) {
            this.shrink();
        }
    }

    shrink() {
        this.isClosing = true;

        const startHeight = `${this.details.offsetHeight}px`;
        const summary = this.details.querySelector('summary');
        const endHeight = `${summary.offsetHeight}px`;

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.details.animate(
            { height: [startHeight, endHeight] },
            { duration: 200, easing: 'ease-out' }
        );

        this.animation.onfinish = () => this.onAnimationFinish(false);
        this.animation.oncancel = () => (this.isClosing = false);
    }

    open() {
        // Exclusividade: fecha os irmãos abertos do mesmo grupo.
        this.#closeSiblings();

        this.details.style.height = `${this.details.offsetHeight}px`;
        this.details.open = true;

        window.requestAnimationFrame(() => this.expand());
    }

    expand() {
        this.isExpanding = true;

        const startHeight = `${this.details.offsetHeight}px`;
        const summary = this.details.querySelector('summary');
        const endHeight = `${summary.offsetHeight + this.content.offsetHeight}px`;

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.details.animate(
            { height: [startHeight, endHeight] },
            { duration: 200, easing: 'ease-out' }
        );

        this.animation.onfinish = () => this.onAnimationFinish(true);
        this.animation.oncancel = () => (this.isExpanding = false);
    }

    onAnimationFinish(open) {
        this.details.open = open;
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.details.style.height = '';
        this.details.style.overflow = '';
    }

    /**
     * Fecha este item (animado se hidratado, instantâneo se não).
     * Chamado por um irmão que está abrindo no modo exclusivo.
     */
    requestClose() {
        const details = this.details || this.querySelector('details');
        if (!details || !details.open || this.isClosing) return;

        if (this.content) {
            details.style.overflow = 'hidden';
            this.shrink();
        } else {
            details.open = false;
        }
    }

    #exclusiveScope() {
        return this.closest('[data-accordion-exclusive]');
    }

    #closeSiblings() {
        const scope = this.#exclusiveScope();
        if (!scope) return;

        scope.querySelectorAll('rk-accordion-element').forEach((el) => {
            if (el !== this && typeof el.requestClose === 'function') {
                el.requestClose();
            }
        });
    }
}

if (!customElements.get('rk-accordion-element')) {
    customElements.define('rk-accordion-element', RkAccordionElement);
}
